import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { config } from '../config';
import { mentors as staticMentors, Mentor } from '../data/mentors';

interface MentorsApiResponse {
  data: Array<{
    id: string;
    name: string;
    email?: string;
    phoneNumber?: string;
    location?: string;
    languages?: string[];
    description?: string;
    detailedDescription?: string;
    profileImageUrl?: string;
    website?: string;
    linkedinUrl?: string;
    twitterUrl?: string;
    availableStages?: string[];
    availablePricing?: string[];
    showOnMobile: boolean;
    isPublic: boolean;
    expertise?: Array<{
      id: string;
      name: string;
      slug: string;
      color: string;
    }>;
    companies?: Array<{
      companyName?: string;
      companyLogoUrl?: string;
      companyWebsite?: string;
    }>;
  }>;
  pagination?: {
    page: number;
    per_page: number;
    total: number;
  };
}

interface UseMentorsOptions {
  expertise?: string;
  location?: string;
  language?: string;
}

interface UseMentorsResult {
  mentors: Mentor[];
  isLoading: boolean;
  error: string | null;
  isUsingFallback: boolean;
}

export function useMentors(options?: UseMentorsOptions): UseMentorsResult {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  useEffect(() => {
    async function fetchMentors() {
      // If API is disabled, use static data immediately
      if (!config.useApi) {
        setMentors(filterStaticMentors(staticMentors, options));
        setIsLoading(false);
        setIsUsingFallback(true);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Build query params
        const params = new URLSearchParams();
        if (options?.expertise) params.set('expertise', options.expertise);
        if (options?.location) params.set('location', options.location);
        if (options?.language) params.set('language', options.language);

        const queryString = params.toString();
        const endpoint = `/api/mentors${queryString ? `?${queryString}` : ''}`;

        const response = await api.get<MentorsApiResponse>(endpoint);

        // Transform API response to match Mentor type
        const transformedMentors: Mentor[] = response.data.map((mentor) => ({
          id: mentor.id,
          name: mentor.name,
          image: mentor.profileImageUrl || '',
          expertise: mentor.expertise?.map((e) => e.name) || [],
          description: mentor.description || '',
          detailedDescription: mentor.detailedDescription || '',
          companies:
            mentor.companies?.map((c) => ({
              name: c.companyName || '',
              logo: c.companyLogoUrl || '',
              website: c.companyWebsite || '',
            })) || [],
          location: mentor.location || '',
          languages: mentor.languages || [],
          email: mentor.email,
          phoneNumber: mentor.phoneNumber,
          website: mentor.website,
          linkedIn: mentor.linkedinUrl,
          twitter: mentor.twitterUrl,
          stages: mentor.availableStages || [],
          price: mentor.availablePricing || [],
          showOnMobile: mentor.showOnMobile,
        }));

        setMentors(transformedMentors);
        setIsUsingFallback(false);
      } catch (err) {
        console.error('Failed to fetch mentors from API:', err);

        if (config.enableFallback) {
          // Fallback to static data
          console.log('Using fallback static data');
          setMentors(filterStaticMentors(staticMentors, options));
          setIsUsingFallback(true);
        } else {
          setError(err instanceof ApiError ? err.message : 'Failed to load mentors');
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchMentors();
  }, [options?.expertise, options?.location, options?.language]);

  return { mentors, isLoading, error, isUsingFallback };
}

// Helper function to filter static mentors (when using fallback)
function filterStaticMentors(mentors: Mentor[], options?: UseMentorsOptions): Mentor[] {
  return mentors.filter((mentor) => {
    if (options?.expertise && !mentor.expertise?.includes(options.expertise)) {
      return false;
    }
    if (options?.location && mentor.location !== options.location) {
      return false;
    }
    if (options?.language && !mentor.languages?.includes(options.language)) {
      return false;
    }
    return true;
  });
}
